import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User as UsuarioFirebase,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../config/firebase';
import { registrarAcesso } from '../servicos/primeiroAcesso';
import type { Papel, Usuario } from '../tipos';

/**
 * Estar autenticado no Firebase NÃO é o mesmo que ter acesso ao ProfitLens.
 *
 * A configuração do Firebase é pública, então qualquer um consegue criar uma
 * conta. O acesso depende de um documento em `usuarios/{uid}` com `ativo: true`,
 * criado por um administrador. Sem ele, a pessoa entra e não vê nada (RN-001).
 *
 * Por isso o contexto expõe três estados distintos, e não um booleano:
 *   carregando       -> ainda não dá para decidir nada
 *   sem sessão       -> vai para o login
 *   sessão sem perfil-> conta órfã: tela explicando que falta autorização
 */
export type EstadoAcesso =
  | 'CARREGANDO'
  | 'DESLOGADO'
  | 'SEM_PERFIL'
  | 'INATIVO'
  | 'LIBERADO';

interface AuthCtx {
  estado: EstadoAcesso;
  usuarioFirebase: UsuarioFirebase | null;
  perfil: Usuario | null;
  papel: Papel | null;
  ehAdministrador: boolean;
  podeEscrever: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  recuperarSenha: (email: string) => Promise<void>;
  reavaliar: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuarioFirebase, setUsuarioFirebase] = useState<UsuarioFirebase | null>(null);
  const [perfil, setPerfil] = useState<Usuario | null>(null);
  const [estado, setEstado] = useState<EstadoAcesso>('CARREGANDO');

  const avaliar = useCallback(async (u: UsuarioFirebase | null) => {
    setUsuarioFirebase(u);
    if (!u) {
      setPerfil(null);
      setEstado('DESLOGADO');
      return;
    }

    const carregarPerfil = async () => {
      try {
        const snap = await getDoc(doc(db, 'usuarios', u.uid));
        return snap.exists() ? ({ id: snap.id, ...snap.data() } as Usuario) : null;
      } catch {
        // As regras negam a leitura para quem não tem perfil. O erro aqui é
        // esperado nesse caso e significa exatamente "sem autorização".
        return null;
      }
    };

    // O perfil não é mais criado aqui. Quem cria é a função `aceitarConvite`,
    // chamada pela tela de primeiro acesso com o token que veio no e-mail —
    // sessão sem perfil significa, simplesmente, convite não aceito.
    const dados = await carregarPerfil();

    if (!dados) {
      setPerfil(null);
      setEstado('SEM_PERFIL');
      return;
    }

    setPerfil(dados);
    setEstado(dados.ativo ? 'LIBERADO' : 'INATIVO');
    if (dados.ativo) void registrarAcesso(u.uid);
  }, []);

  useEffect(() => onAuthStateChanged(auth, (u) => void avaliar(u)), [avaliar]);

  const valor = useMemo<AuthCtx>(
    () => ({
      estado,
      usuarioFirebase,
      perfil,
      papel: perfil?.papel ?? null,
      ehAdministrador: perfil?.papel === 'ADMINISTRADOR',
      podeEscrever: perfil?.papel === 'ADMINISTRADOR' || perfil?.papel === 'OPERADOR',
      entrar: async (email, senha) => {
        await signInWithEmailAndPassword(auth, email.trim(), senha);
      },
      sair: async () => {
        await signOut(auth);
      },
      /**
       * O link continua sendo gerado pelo Firebase, mas quem entrega é o
       * Resend, com remetente do nosso domínio. Mensagem de um domínio que a
       * pessoa não reconhece é o jeito mais rápido de e-mail legítimo virar
       * spam ignorado.
       *
       * A função responde igual existindo ou não a conta: dizer "este e-mail
       * não existe" transformaria esta tela num verificador de quem tem conta.
       */
      recuperarSenha: async (email) => {
        const chamar = httpsCallable<{ email: string }, { situacao: string }>(
          functions,
          'recuperarSenha',
        );
        await chamar({ email: email.trim() });
      },
      // Recarrega o token e reavalia — usado depois de aceitar um convite.
      reavaliar: async () => {
        setEstado('CARREGANDO');
        await auth.currentUser?.reload();
        await auth.currentUser?.getIdToken(true);
        await avaliar(auth.currentUser);
      },
    }),
    [estado, usuarioFirebase, perfil, avaliar],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
